import React from 'react';
import toast from 'react-hot-toast';
import { BASE_URL } from '../../utils/api';
import {  QrCode, Upload, Trash2 , Loader2 } from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = React.useState({
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPass: '',
    adminNotificationEmail: '',
    paymentQrCode: ''
  });
  const [loading, setLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [savingQr, setSavingQr] = React.useState(false);

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { default: api } = await import('../../utils/api');
        const res = await api.get('/settings');
        if (res.data) {
          setSettings({
            smtpHost: res.data.smtpHost || '',
            smtpPort: res.data.smtpPort || '',
            smtpUser: res.data.smtpUser || '',
            smtpPass: res.data.smtpPass || '',
            adminNotificationEmail: res.data.adminNotificationEmail || res.data.admin_notification_email || '',
            paymentQrCode: res.data.paymentQrCode || ''
          });
        }
      } catch (err) {
        console.error('Failed to fetch settings', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSaveSettings = async (e, successMsg = 'Settings saved successfully!') => {
    if (e) e.preventDefault();
    setIsSaving(true);
    try {
      const { default: api } = await import('../../utils/api');
      await api.post('/settings', settings);
      toast.success(successMsg);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        toast.error('Please fix the errors in the form');
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to save settings');
      }
    }
  };

  const handleQrImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        return toast.error("Please upload a valid image file (PNG, JPG, WEBP)");
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings(prev => ({ ...prev, paymentQrCode: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveQrCode = async () => {
    try {
      setSavingQr(true);
      const { default: api } = await import('../../utils/api');
      await api.post('/settings', { paymentQrCode: settings.paymentQrCode });
      // Re-fetch to get updated file path if base64 was processed
      const res = await api.get('/settings');
      if (res.data && res.data.paymentQrCode) {
        setSettings(prev => ({ ...prev, paymentQrCode: res.data.paymentQrCode }));
      }
      toast.success('Payment QR Code updated successfully!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save Payment QR Code');
    } finally {
      setSavingQr(false);
    }
  };

  const getQrImageUrl = () => {
    if (!settings.paymentQrCode || typeof settings.paymentQrCode !== 'string') return null;
    const val = settings.paymentQrCode.trim();
    if (!val) return null;
    if (val.startsWith('data:') || val.startsWith('http://') || val.startsWith('https://')) {
      return val;
    }
    const cleanPath = val.startsWith('/') ? val : `/${val}`;
    return `${BASE_URL}${cleanPath}`;
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Settings</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>
        
        {/* Left Column: Payment QR Code & Notification Settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Payment QR Code Settings */}
          <div className="card">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <QrCode size={20} color="var(--color-primary)" /> Payment QR Code
            </h3>
            <p className="text-sm text-muted mb-4">
              Upload the company payment QR code (UPI / GPay / PhonePe / Bank QR) to display in Delivery Details for customers and delivery boys.
            </p>

            <div className="flex flex-col gap-4">
              {settings.paymentQrCode && getQrImageUrl() ? (
                <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: '#f8fafc', textAlign: 'center' }}>
                  <img 
                    src={getQrImageUrl()} 
                    alt="Payment QR Code" 
                    onError={(e) => {
                      console.error("Failed to load QR image:", getQrImageUrl());
                    }}
                    style={{ maxWidth: '220px', maxHeight: '220px', objectFit: 'contain', margin: '0 auto', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', padding: '8px', display: 'block' }} 
                  />
                  <div className="flex gap-2 justify-center mt-3">
                    <label className="btn text-sm" style={{ border: '1px solid var(--color-border)', backgroundColor: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Upload size={14} /> Replace QR Image
                      <input type="file" accept="image/*" onChange={handleQrImageChange} style={{ display: 'none' }} />
                    </label>
                    <button 
                      type="button" 
                      className="btn text-danger text-sm" 
                      onClick={async () => {
                        setSettings(prev => ({ ...prev, paymentQrCode: '' }));
                        try {
                          const { default: api } = await import('../../utils/api');
                          await api.post('/settings', { paymentQrCode: '' });
                          toast.success("Payment QR Code removed");
                        } catch (err) {
                          toast.error("Failed to remove QR code");
                        }
                      }}
                      style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}
                    >
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ border: '2px dashed var(--color-border)', borderRadius: '8px', padding: '2rem', textAlign: 'center', backgroundColor: '#fafafa' }}>
                  <QrCode size={36} color="#94a3b8" style={{ margin: '0 auto 0.5rem auto' }} />
                  <p className="text-sm font-semibold text-main mb-1">No Payment QR Code Uploaded</p>
                  <p className="text-xs text-muted mb-3">Upload your UPI / Payment QR code image</p>
                  <label className="btn btn-primary text-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Upload size={16} /> Choose Image
                    <input type="file" accept="image/*" onChange={handleQrImageChange} style={{ display: 'none' }} />
                  </label>
                </div>
              )}

              {settings.paymentQrCode && (
                <button 
                  type="button" 
                  className="btn btn-primary mt-2" 
                  onClick={handleSaveQrCode} 
                  disabled={savingQr || loading}
                >
                  {savingQr ? 'Saving...' : 'Save Payment QR Code'}
                </button>
              )}
            </div>
          </div>

          {/* Notification Settings */}
          <div className="card">
            <h3 className="font-semibold mb-4">Notification Settings</h3>
            <form className="flex-col gap-4" noValidate>
              <div>
                <label className="text-sm font-semibold mb-1 block">Admin Email for Lead Generation Notifications <span style={{color: 'var(--color-danger)'}}>*</span></label>
                <input type="email" required className="input-field" value={settings.adminNotificationEmail || ''} onChange={(e) => setSettings({...settings, adminNotificationEmail: e.target.value})} placeholder="admin@agency.com" />
              </div>
              <button type="button" className="btn btn-primary mt-2" onClick={(e) => {
                const newErrors = {};
                if (!settings.adminNotificationEmail) newErrors.adminNotificationEmail = 'Admin Email is required';
                if (Object.keys(newErrors).length > 0) {
                  setErrors(newErrors);
                  return toast.error('Please fix the errors in the form');
                }
                setErrors({});
                handleSaveSettings(e, 'Notification Settings saved!');

              }} disabled={loading || isSaving}>
                {isSaving && <Loader2 size={16} className="spinner-icon" />}
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </form>
          </div>

        </div>

        {/* Right Column: SMTP Settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="card">
            <h3 className="font-semibold mb-4">SMTP Settings</h3>
            <form className="flex-col gap-4" noValidate>
              <div>
                <label className="text-sm font-semibold mb-1 block">SMTP Host <span style={{color: 'var(--color-danger)'}}>*</span></label>
                <input type="text" required className="input-field" value={settings.smtpHost || ''} onChange={(e) => setSettings({...settings, smtpHost: e.target.value})} placeholder="smtp.gmail.com" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">SMTP Port <span style={{color: 'var(--color-danger)'}}>*</span></label>
                <input type="number" required className="input-field" value={settings.smtpPort || ''} onChange={(e) => setSettings({...settings, smtpPort: e.target.value})} placeholder="587" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Username / Email <span style={{color: 'var(--color-danger)'}}>*</span></label>
                <input type="email" required className="input-field" value={settings.smtpUser || ''} onChange={(e) => setSettings({...settings, smtpUser: e.target.value})} placeholder="hello@agency.com" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1 block">Password <span style={{color: 'var(--color-danger)'}}>*</span></label>
                <input type="password" required className="input-field" value={settings.smtpPass || ''} onChange={(e) => setSettings({...settings, smtpPass: e.target.value})} placeholder="*********" />
              </div>
              <button type="button" className="btn btn-primary mt-2" onClick={(e) => {
                const newErrors = {};
                if (!settings.smtpHost) newErrors.smtpHost = 'SMTP Host is required';
                if (!settings.smtpPort) newErrors.smtpPort = 'SMTP Port is required';
                if (!settings.smtpUser) newErrors.smtpUser = 'Username / Email is required';
                if (!settings.smtpPass) newErrors.smtpPass = 'Password is required';
                if (Object.keys(newErrors).length > 0) {
                  setErrors(newErrors);
                  return toast.error('Please fix the errors in the form');
                }
                setErrors({});
                handleSaveSettings(e, 'SMTP Settings saved!');

              }} disabled={loading || isSaving}>
                {isSaving && <Loader2 size={16} className="spinner-icon" />}
                {isSaving ? 'Saving...' : 'Save SMTP'}
              </button>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Settings;
